import { createTheme } from '@mui/material/styles'
import * as colors from '@mui/material/colors'

const royalPurple = '#5b3fd6'
const royalGold = '#f0c66a'
const royalCrimson = '#d83f5f'
const feltGreen = '#0f8b62'

/** @param {'dark' | 'light'} mode */
export function createAdminTheme(mode = 'dark') {
  const isDark = mode === 'dark'
  return createTheme({
    palette: {
      mode,
      primary: { main: royalPurple, light: '#7b64ee', dark: '#34216f', contrastText: colors.common.white },
      secondary: { main: royalGold, light: '#ffe39a', dark: '#9d6d1f', contrastText: '#140e1f' },
      error: { main: royalCrimson },
      success: { main: feltGreen },
      background: {
        default: isDark ? '#070912' : '#f7f1e4',
        paper: isDark ? '#111420' : '#fffaf0',
      },
      text: {
        primary: isDark ? '#fff6dc' : '#21182f',
        secondary: isDark ? 'rgba(255,246,220,0.68)' : 'rgba(33,24,47,0.68)',
      },
      divider: isDark ? 'rgba(240,198,106,0.18)' : 'rgba(91,63,214,0.16)',
    },
    typography: {
      fontFamily: ['"Source Sans 3"', 'Source Sans Pro', 'Inter', 'system-ui', 'sans-serif'].join(','),
      fontSize: 13,
      body1: { fontSize: '0.875rem', lineHeight: 1.45 },
      body2: { fontSize: '0.8125rem', lineHeight: 1.45 },
      h5: { fontWeight: 800, letterSpacing: '-0.02em' },
      h6: { fontWeight: 800, fontSize: '1rem', letterSpacing: '-0.01em' },
      button: { fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase' },
      overline: { fontWeight: 800, letterSpacing: '0.11em' },
    },
    shape: { borderRadius: 14 },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            background:
              isDark
                ? 'radial-gradient(circle at top left, rgba(91,63,214,0.22), transparent 34%), radial-gradient(circle at top right, rgba(240,198,106,0.12), transparent 32%), #070912'
                : 'radial-gradient(circle at top left, rgba(240,198,106,0.3), transparent 34%), #f7f1e4',
          },
          '*': {
            scrollbarWidth: 'thin',
            scrollbarColor: isDark ? '#5b3fd6 #0b0e18' : '#b78a31 #f7f1e4',
          },
          '*::-webkit-scrollbar': { width: 9, height: 9 },
          '*::-webkit-scrollbar-thumb': {
            borderRadius: 999,
            background: isDark ? 'linear-gradient(180deg, #f0c66a, #5b3fd6)' : '#b78a31',
          },
          '*::-webkit-scrollbar-track': { background: isDark ? '#0b0e18' : '#f7f1e4' },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            borderColor: isDark ? 'rgba(240,198,106,0.18)' : 'rgba(91,63,214,0.14)',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            border: `1px solid ${isDark ? 'rgba(240,198,106,0.18)' : 'rgba(91,63,214,0.14)'}`,
            boxShadow: isDark ? '0 18px 42px rgba(0,0,0,0.34)' : '0 18px 36px rgba(58,42,95,0.1)',
          },
        },
      },
      MuiButton: {
        defaultProps: { size: 'small', disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 999,
          },
          containedPrimary: {
            background: `linear-gradient(135deg, ${royalPurple}, #7b64ee 56%, #9b6dff)`,
            boxShadow: '0 12px 30px rgba(91,63,214,0.3)',
            '&:hover': {
              background: `linear-gradient(135deg, #4930bf, ${royalPurple} 58%, #876eff)`,
              boxShadow: '0 16px 34px rgba(91,63,214,0.38)',
            },
          },
          outlined: {
            borderColor: isDark ? 'rgba(240,198,106,0.45)' : 'rgba(91,63,214,0.32)',
          },
        },
      },
      MuiTextField: {
        defaultProps: { size: 'small', margin: 'dense' },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            backgroundColor: isDark ? 'rgba(255,255,255,0.035)' : 'rgba(255,255,255,0.72)',
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: royalGold,
              boxShadow: '0 0 0 3px rgba(240,198,106,0.14)',
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 999,
            fontWeight: 800,
          },
          outlined: {
            borderColor: isDark ? 'rgba(240,198,106,0.32)' : 'rgba(91,63,214,0.24)',
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: isDark ? '#17101f' : '#fffaf0',
            border: isDark ? '1px solid rgba(240,198,106,0.35)' : '1px solid rgba(91,63,214,0.18)',
            color: isDark ? '#fff6dc' : '#21182f',
            boxShadow: isDark ? '0 16px 36px rgba(0,0,0,0.45)' : '0 16px 32px rgba(73,47,25,0.14)',
          },
          arrow: {
            color: isDark ? '#17101f' : '#fffaf0',
          },
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          root: {
            borderColor: isDark ? 'rgba(240,198,106,0.18)' : 'rgba(91,63,214,0.14)',
          },
        },
      },
    },
  })
}

export const theme = createAdminTheme('light')
